import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

import {
  ensureWeightForSingleSection,
  hasCheckboxScore,
  hasOptionScore,
  hasRangeScore,
  isNumberQuestion,
  isOptionQuestion,
  isScoringEligibleQuestion,
  isScaleQuestion,
  isTextLikeQuestion,
  OPTION_ITEM_TYPES,
  resolveScoringItemId
} from './scoringShared'
import { toNullableNumber } from '../utils'
import { normalizeVisibilityRulesPayload } from '../conditionalLogic/normalizeVisibilityRulePayload'

function buildRequiredOptionIdsPayload(options = []) {
  const requiredOptionIds = (Array.isArray(options) ? options : []).reduce(
    (accumulator, option) => {
      const optionId = utils.getEntityId(option)

      if (!option?.countsTowardScore || !optionId) {
        return accumulator
      }

      accumulator.push(optionId)
      return accumulator
    },
    []
  )

  return requiredOptionIds.length ? requiredOptionIds : null
}

function buildOptionPointsPayload(options = []) {
  const normalizedOptions = Array.isArray(options) ? options : []
  const hasConfiguredOptionScore = normalizedOptions.some(option => {
    const points = toNullableNumber(option?.scoringPoints)
    return points !== null && points >= 0
  })

  if (!hasConfiguredOptionScore) {
    return null
  }

  const optionPoints = normalizedOptions.reduce((accumulator, option) => {
    const normalizedPoints = toNullableNumber(option?.scoringPoints)
    const points = normalizedPoints === null ? 0 : normalizedPoints
    const optionId = utils.getEntityId(option)

    if (points < 0 || !optionId) {
      return accumulator
    }

    accumulator.push({ optionId, points })
    return accumulator
  }, [])

  return optionPoints.length ? optionPoints : null
}

function hasUnpersistedScoredOptions(item) {
  return (Array.isArray(item?.options) ? item.options : []).some(option => {
    const points = toNullableNumber(option?.scoringPoints)
    const optionId = utils.getEntityId(option)
    return points !== null && points >= 0 && !optionId
  })
}

function hasUnpersistedSelectedCheckboxScoringOption(item) {
  return (Array.isArray(item?.options) ? item.options : []).some(option => {
    const optionId = utils.getEntityId(option)
    return option?.countsTowardScore && !optionId
  })
}

function hasUnsavedScoringDependencies(item) {
  return (
    (OPTION_ITEM_TYPES.has(item?.type) && hasUnpersistedScoredOptions(item)) ||
    (item?.type === ITEM_TYPES.CHECKBOXES &&
      hasUnpersistedSelectedCheckboxScoringOption(item))
  )
}

function requiresScoringFollowUpPersist(checklist = {}) {
  if (
    checklist?.scoringType !== 'question' &&
    checklist?.scoringType !== 'section'
  ) {
    return false
  }

  return (Array.isArray(checklist?.items) ? checklist.items : []).some(
    hasUnsavedScoringDependencies
  )
}

function buildSeparatorScoringPayload(item) {
  const weightPercentage = toNullableNumber(item?.scoring?.weightPercentage)

  if (weightPercentage === null) {
    return null
  }

  return {
    weightPercentage,
  }
}

function buildTextLikeScoringPayload(item) {
  const pointsIfAnswered = toNullableNumber(item?.scoring?.pointsIfAnswered)

  if (pointsIfAnswered === null) {
    return null
  }

  return {
    rule: {
      pointsIfAnswered,
    },
  }
}

function buildOptionQuestionScoringPayload(item) {
  if (!hasOptionScore(item)) {
    return null
  }

  const optionPoints = buildOptionPointsPayload(item?.options)

  if (!optionPoints) {
    return null
  }

  return {
    rule: {
      optionPoints,
    },
  }
}

function buildCheckboxScoringPayload(item) {
  if (!hasCheckboxScore(item)) {
    return null
  }

  const requiredOptionIds = buildRequiredOptionIdsPayload(item?.options)
  const pointsIfAllSelected = toNullableNumber(
    item?.scoring?.pointsIfAllSelected
  )

  if (!requiredOptionIds || pointsIfAllSelected === null) {
    return null
  }

  return {
    rule: {
      requiredOptionIds,
      pointsIfAllSelected,
      pointsIfMissing: 0,
    },
  }
}

function buildRangeQuestionScoringPayload({ min, max, pointsIfWithinRange }) {
  if (!hasRangeScore({ min, max, pointsIfWithinRange })) {
    return null
  }

  return {
    rule: {
      range: {
        min,
        max,
      },
      pointsIfWithinRange,
      pointsIfOutOfRange: 0,
    },
  }
}

function buildChecklistItemScoringPayload(item) {
  if (item?.type === ITEM_TYPES.SEPARATOR) {
    return buildSeparatorScoringPayload(item)
  }

  if (!isScoringEligibleQuestion(item)) {
    return null
  }

  if (isTextLikeQuestion(item)) {
    return buildTextLikeScoringPayload(item)
  }

  if (isOptionQuestion(item)) {
    return buildOptionQuestionScoringPayload(item)
  }

  if (isNumberQuestion(item) || isScaleQuestion(item)) {
    return buildRangeQuestionScoringPayload({
      min: toNullableNumber(item?.scoring?.range?.min),
      max: toNullableNumber(item?.scoring?.range?.max),
      pointsIfWithinRange: toNullableNumber(item?.scoring?.pointsIfWithinRange),
    })
  }

  if (item?.type === ITEM_TYPES.CHECKBOXES) {
    return buildCheckboxScoringPayload(item)
  }

  return null
}

function buildNormalizedChecklistItemPayload(
  item,
  { isConditionalLogicEnabled = false } = {}
) {
  const checklistItemPayload = {
    ...item,
    referenceMedia: item?.referenceMedia?.map?.(media => media?.id || media),
    options: item?.options?.map?.(option => {
      const normalizedOption = { ...option }
      delete normalizedOption.scoringPoints
      delete normalizedOption.countsTowardScore
      return normalizedOption
    }),
  }

  // Flag off: rules are not part of the contract yet, so rule-less items keep
  // today's payload shape (no key). But if an item already carries rules —
  // only possible after a flag rollback — preserve them in their canonical
  // shape instead of leaking raw editor state through the item spread.
  const visibilityRules = normalizeVisibilityRulesPayload(item?.visibilityRules)
  delete checklistItemPayload.visibilityRule
  delete checklistItemPayload.visibilityRules

  if (isConditionalLogicEnabled || visibilityRules.length > 0) {
    checklistItemPayload.visibilityRules = visibilityRules
  }

  delete checklistItemPayload.id
  delete checklistItemPayload.scoringItemId
  delete checklistItemPayload.isDefaultSection

  return checklistItemPayload
}

function resolveChecklistItemScoringPayload({
  item,
  omitUnsavedOptionScoring,
  fallbackItem,
  preserveFallbackScoring,
}) {
  const scoring = buildChecklistItemScoringPayload(item)
  const fallbackScoring =
    preserveFallbackScoring && fallbackItem
      ? buildChecklistItemScoringPayload(fallbackItem)
      : null
  const hasUnsavedScoring =
    omitUnsavedOptionScoring && hasUnsavedScoringDependencies(item)

  if (hasUnsavedScoring) {
    return fallbackScoring
  }

  if (scoring) {
    return scoring
  }

  return null
}

function applyChecklistItemScoringPayload(checklistItemPayload, scoring) {
  if (!scoring) {
    delete checklistItemPayload.scoring
    return checklistItemPayload
  }

  checklistItemPayload.scoring = scoring
  return checklistItemPayload
}

function buildChecklistItemPayload(item, options = {}) {
  const {
    isConditionalLogicEnabled = false,
    omitUnsavedOptionScoring = false,
    fallbackItem = null,
    preserveFallbackScoring = false,
  } = options
  const checklistItemPayload = buildNormalizedChecklistItemPayload(item, {
    isConditionalLogicEnabled,
  })
  const scoring = resolveChecklistItemScoringPayload({
    item,
    omitUnsavedOptionScoring,
    fallbackItem,
    preserveFallbackScoring,
  })

  return applyChecklistItemScoringPayload(checklistItemPayload, scoring)
}

function buildRoutineChecklistPayload({
  checklist = {},
  routineName,
  translate,
  isPrivate,
  site,
  entities,
  isConditionalLogicEnabled = false,
  omitUnsavedOptionScoring = false,
  fallbackChecklist = null,
  preserveFallbackScoring = false,
}) {
  const fallbackItems = Array.isArray(fallbackChecklist?.items)
    ? fallbackChecklist.items
    : []
  const checklistItems =
    checklist?.scoringType === 'section'
      ? ensureWeightForSingleSection(checklist?.items)
      : checklist?.items || []
  const fallbackItemsById = new Map(
    fallbackItems.map((item, index) => [
      resolveScoringItemId(item, `item-${index}`),
      item,
    ])
  )

  return {
    name: translate('checklist.checklist_name', {
      routine: routineName,
    }),
    items: checklistItems.map((item, index) => {
      const fallbackItem = fallbackItemsById.get(
        resolveScoringItemId(item, `item-${index}`)
      )

      return buildChecklistItemPayload(item, {
        isConditionalLogicEnabled,
        omitUnsavedOptionScoring,
        fallbackItem,
        preserveFallbackScoring,
      })
    }),
    scoringType: checklist?.scoringType ?? null,
    isEnabled: !isPrivate,
    isPrivate,
    ...(site ? { site } : {}),
    ...(entities ? { entities } : {}),
  }
}

export {
  buildChecklistItemPayload,
  buildChecklistItemScoringPayload,
  buildRoutineChecklistPayload,
  requiresScoringFollowUpPersist
}
